import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, Trash2 } from "lucide-react";
import styles from "./index.module.scss";

const DateRangePicker = ({
  selectionRange = { startDate: null, endDate: null },
  onFromDateChange,
  onToDateChange,
  onResetDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState({ right: 0 });
  const containerRef = useRef(null);
  const modalRef = useRef(null);

  // Initialize dates from props
  const getInitialDates = () => {
    return {
      startDate: selectionRange?.startDate || null,
      endDate: selectionRange?.endDate || null,
      startViewDate: selectionRange?.startDate
        ? new Date(
            selectionRange.startDate.getFullYear(),
            selectionRange.startDate.getMonth(),
            1
          )
        : new Date(),
      endViewDate: selectionRange?.endDate
        ? new Date(
            selectionRange.endDate.getFullYear(),
            selectionRange.endDate.getMonth(),
            1
          )
        : new Date(),
    };
  };

  const [dates, setDates] = useState(getInitialDates);
  const [yearRanges, setYearRanges] = useState({
    start: { start: 2016, end: 2039 },
    end: { start: 2016, end: 2039 },
  });
  const [openDropdowns, setOpenDropdowns] = useState({
    fixed: false,
    startMonthYear: false,
    endMonthYear: false,
  });
  const [viewMode, setViewMode] = useState({
    start: "month",
    end: "month",
  });

  const fixedDropdownRef = useRef(null);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const monthAbbr = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];
  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  // Update dates when selectionRange prop changes
  useEffect(() => {
    const initial = {
      startDate: selectionRange?.startDate || null,
      endDate: selectionRange?.endDate || null,
      startViewDate: selectionRange?.startDate
        ? new Date(
            selectionRange.startDate.getFullYear(),
            selectionRange.startDate.getMonth(),
            1
          )
        : new Date(),
      endViewDate: selectionRange?.endDate
        ? new Date(
            selectionRange.endDate.getFullYear(),
            selectionRange.endDate.getMonth(),
            1
          )
        : new Date(),
    };
    setDates(initial);
  }, [selectionRange]);

  // Calculate modal position before opening
  const calculateModalPosition = useCallback(() => {
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const modalWidth = 520;
      const viewportWidth = window.innerWidth;

      const spaceOnRight = viewportWidth - containerRect.right;
      const spaceOnLeft = containerRect.left;

      if (spaceOnRight < modalWidth && spaceOnLeft > spaceOnRight) {
        return { right: 0, left: "auto" };
      } else {
        return { left: 0, right: "auto" };
      }
    }
    return { right: 0, left: "auto" };
  }, []);

  const handleToggleModal = () => {
    if (!isOpen) {
      // Calculate position before opening
      const position = calculateModalPosition();
      setModalPosition(position);
    }
    setIsOpen(!isOpen);
  };

  // Recalculate position on window resize when modal is open
  useEffect(() => {
    if (isOpen) {
      const handleResize = () => {
        const position = calculateModalPosition();
        setModalPosition(position);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen, calculateModalPosition]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        const initial = {
          startDate: selectionRange?.startDate || null,
          endDate: selectionRange?.endDate || null,
          startViewDate: selectionRange?.startDate
            ? new Date(
                selectionRange.startDate.getFullYear(),
                selectionRange.startDate.getMonth(),
                1
              )
            : new Date(),
          endViewDate: selectionRange?.endDate
            ? new Date(
                selectionRange.endDate.getFullYear(),
                selectionRange.endDate.getMonth(),
                1
              )
            : new Date(),
        };
        setDates(initial);
        setOpenDropdowns({
          fixed: false,
          startMonthYear: false,
          endMonthYear: false,
        });
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, selectionRange]);

  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfWeek = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const renderCalendarDays = (type) => {
    const viewDate = type === "start" ? dates.startViewDate : dates.endViewDate;
    const selectedDate = type === "start" ? dates.startDate : dates.endDate;
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfWeek(year, month);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className={styles.dayCellOtherMonth}></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const cellDate = new Date(year, month, day);
      const isSelected = isSameDate(cellDate, selectedDate);
      const isInRange =
        dates.startDate &&
        dates.endDate &&
        cellDate >= dates.startDate &&
        cellDate <= dates.endDate &&
        !isSameDate(cellDate, dates.startDate) &&
        !isSameDate(cellDate, dates.endDate);

      days.push(
        <div
          key={`day-${day}`}
          className={`${styles.dayCell} ${
            isSelected ? styles.dayCellSelected : ""
          } ${isInRange ? styles.dayCellInRange : ""}`}
          onClick={() => handleDateSelect(type, day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const handleDateSelect = (type, day) => {
    const viewDate = type === "start" ? dates.startViewDate : dates.endViewDate;
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);

    setDates((prev) => {
      let newStartDate = prev.startDate;
      let newEndDate = prev.endDate;

      if (type === "start") {
        newStartDate = newDate;
        if (prev.endDate && newDate > prev.endDate) {
          newEndDate = newDate;
        }
      } else {
        newEndDate = newDate;
        if (prev.startDate && newDate < prev.startDate) {
          newStartDate = newDate;
        }
      }

      return {
        ...prev,
        startDate: newStartDate,
        endDate: newEndDate,
      };
    });
  };

  const navigateMonth = (type, direction) => {
    setDates((prev) => {
      const viewDate = type === "start" ? prev.startViewDate : prev.endViewDate;
      const newViewDate = new Date(viewDate);

      if (direction === "prev") {
        newViewDate.setMonth(newViewDate.getMonth() - 1);
      } else {
        newViewDate.setMonth(newViewDate.getMonth() + 1);
      }

      return {
        ...prev,
        [type === "start" ? "startViewDate" : "endViewDate"]: newViewDate,
      };
    });
  };

  const handleMonthSelect = (type, month) => {
    setDates((prev) => {
      const viewDate = type === "start" ? prev.startViewDate : prev.endViewDate;
      const newViewDate = new Date(viewDate);
      newViewDate.setMonth(month);

      return {
        ...prev,
        [type === "start" ? "startViewDate" : "endViewDate"]: newViewDate,
      };
    });
    setOpenDropdowns((prev) => ({
      ...prev,
      [type === "start" ? "startMonthYear" : "endMonthYear"]: false,
    }));
    setViewMode((prev) => ({ ...prev, [type]: "month" }));
  };

  const handleYearSelect = (type, year) => {
    setDates((prev) => {
      const viewDate = type === "start" ? prev.startViewDate : prev.endViewDate;
      const newViewDate = new Date(viewDate);
      newViewDate.setFullYear(year);

      return {
        ...prev,
        [type === "start" ? "startViewDate" : "endViewDate"]: newViewDate,
      };
    });
    setOpenDropdowns((prev) => ({
      ...prev,
      [type === "start" ? "startMonthYear" : "endMonthYear"]: false,
    }));
    setViewMode((prev) => ({ ...prev, [type]: "month" }));
  };

  const navigateYearRange = (type, direction) => {
    setYearRanges((prev) => {
      const range = prev[type];
      const rangeSize = range.end - range.start + 1;

      if (direction === "prev") {
        return {
          ...prev,
          [type]: {
            start: range.start - rangeSize,
            end: range.end - rangeSize,
          },
        };
      } else {
        return {
          ...prev,
          [type]: {
            start: range.start + rangeSize,
            end: range.end + rangeSize,
          },
        };
      }
    });
  };

  const applyPreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case "last7":
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        end = new Date(today);
        break;
      case "last30":
        start = new Date(today);
        start.setDate(today.getDate() - 30);
        end = new Date(today);
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case "lastMonth":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "thisYear":
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setDates({
      startDate: start,
      endDate: end,
      startViewDate: new Date(start.getFullYear(), start.getMonth(), 1),
      endViewDate: new Date(end.getFullYear(), end.getMonth(), 1),
    });
    setOpenDropdowns((prev) => ({ ...prev, fixed: false }));
  };

  const handleCancel = () => {
    const initial = getInitialDates();
    setDates(initial);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (onFromDateChange && dates.startDate) {
      onFromDateChange(dates.startDate);
    }
    if (onToDateChange && dates.endDate) {
      onToDateChange(dates.endDate);
    }
    setIsOpen(false);
  };

  const handleReset = () => {
    const today = new Date();
    const resetDates = {
      startDate: null,
      endDate: null,
      startViewDate: new Date(today.getFullYear(), today.getMonth(), 1),
      endViewDate: new Date(today.getFullYear(), today.getMonth(), 1),
    };
    setDates(resetDates);
    
    // Call the parent reset handler if provided
    if (onResetDate) {
      onResetDate();
    }
    
    // Also reset the dates in parent component
    if (onFromDateChange) {
      onFromDateChange(null);
    }
    if (onToDateChange) {
      onToDateChange(null);
    }
  };

  const toggleDropdown = (dropdownName) => {
    setOpenDropdowns((prev) => {
      const newState = { ...prev };

      if (
        dropdownName === "startMonthYear" ||
        dropdownName === "endMonthYear"
      ) {
        newState.startMonthYear =
          dropdownName === "startMonthYear" ? !prev.startMonthYear : false;
        newState.endMonthYear =
          dropdownName === "endMonthYear" ? !prev.endMonthYear : false;
      } else {
        newState[dropdownName] = !prev[dropdownName];
      }

      return newState;
    });
  };

  const showYearView = (type) => {
    setViewMode((prev) => ({ ...prev, [type]: "year" }));
    const viewDate = type === "start" ? dates.startViewDate : dates.endViewDate;
    const currentYear = viewDate.getFullYear();
    const range = yearRanges[type];

    if (currentYear < range.start || currentYear > range.end) {
      const rangeSize = range.end - range.start + 1;
      const halfRange = Math.floor(rangeSize / 2);
      setYearRanges((prev) => ({
        ...prev,
        [type]: {
          start: currentYear - halfRange,
          end: currentYear + halfRange,
        },
      }));
    }
  };

  const getMonthYearText = (type) => {
    const viewDate = type === "start" ? dates.startViewDate : dates.endViewDate;
    const month = months[viewDate.getMonth()];
    const year = viewDate.getFullYear();
    return `${month.toUpperCase()} ${year}`;
  };

  const renderMonthSelector = (type) => {
    const viewDate = type === "start" ? dates.startViewDate : dates.endViewDate;
    const currentMonth = viewDate.getMonth();
    const currentYear = viewDate.getFullYear();

    return (
      <div className={styles.monthSelectorView}>
        <div
          className={styles.monthSelectorYearButton}
          onClick={() => showYearView(type)}
        >
          <span>{currentYear}</span>
          <span>▲</span>
        </div>
        <div className={styles.monthSelector}>
          {monthAbbr.map((month, index) => (
            <div
              key={month}
              className={`${styles.monthOption} ${
                index === currentMonth ? styles.monthOptionSelected : ""
              }`}
              onClick={() => handleMonthSelect(type, index)}
            >
              {month}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderYearSelector = (type) => {
    const range = yearRanges[type];
    const viewDate = type === "start" ? dates.startViewDate : dates.endViewDate;
    const currentYear = viewDate.getFullYear();
    const years = [];

    for (let year = range.start; year <= range.end; year++) {
      years.push(year);
    }

    return (
      <div className={`${styles.yearSelectorView} ${styles.show}`}>
        <div className={styles.monthYearDropdownHeader}>
          <div className={styles.yearRangeNav}>
            <button
              className={styles.yearRangeArrow}
              onClick={() => navigateYearRange(type, "prev")}
            >
              ‹
            </button>
            <span className={styles.yearRangeText}>
              {range.start} - {range.end}
            </span>
            <button
              className={styles.yearRangeArrow}
              onClick={() => navigateYearRange(type, "next")}
            >
              ›
            </button>
          </div>
        </div>
        <div className={styles.yearSelector}>
          {years.map((year) => (
            <div
              key={year}
              className={`${styles.yearOption} ${
                year === currentYear ? styles.yearOptionSelected : ""
              }`}
              onClick={() => handleYearSelect(type, year)}
            >
              {year}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getDateRangeLabel = () => {
    if (selectionRange?.startDate && selectionRange?.endDate) {
      const start = selectionRange.startDate.toLocaleDateString();
      const end = selectionRange.endDate.toLocaleDateString();
      return `${start} - ${end}`;
    }
    return "Select date range";
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${
      months[date.getMonth()]
    } ${date.getDate()}, ${date.getFullYear()}`;
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <div onClick={handleToggleModal} className={styles.triggerButton}>
        {getDateRangeLabel()} <ChevronDown size={18} />
      </div>

      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={handleCancel}></div>
          <div ref={modalRef} className={styles.modal} style={modalPosition}>
          <div className={styles.modalContent}>
          {/* Date Input Fields */}
          <div className={styles.dateInputs}>
            <div className={styles.dateInput}>
              <label>Start Date</label>
              <input
                type="text"
                value={formatDateForInput(dates.startDate)}
                readOnly
                placeholder="Select start date"
              />
            </div>
            <div className={styles.dateInput}>
              <label>End Date</label>
              <input
                type="text"
                value={formatDateForInput(dates.endDate)}
                readOnly
                placeholder="Select end date"
              />
            </div>
          </div>

          {/* Preset Dropdown and Reset Button */}
          <div className={styles.datePickerHeader}>
            <div className={styles.headerControls}>
              <div className={styles.fixedDropdown} ref={fixedDropdownRef}>
                <button
                  className={styles.fixedDropdownButton}
                  onClick={() => toggleDropdown("fixed")}
                >
                  Auto date range <span>▼</span>
                </button>
                {openDropdowns.fixed && (
                  <div className={`${styles.fixedDropdownMenu} ${styles.show}`}>
                    <div
                      className={styles.fixedDropdownMenuItem}
                      onClick={() => applyPreset("last7")}
                    >
                      Last 7 days
                    </div>
                    <div
                      className={styles.fixedDropdownMenuItem}
                      onClick={() => applyPreset("last30")}
                    >
                      Last 30 days
                    </div>
                    <div
                      className={styles.fixedDropdownMenuItem}
                      onClick={() => applyPreset("thisMonth")}
                    >
                      This month
                    </div>
                    <div
                      className={styles.fixedDropdownMenuItem}
                      onClick={() => applyPreset("lastMonth")}
                    >
                      Last month
                    </div>
                    <div
                      className={styles.fixedDropdownMenuItem}
                      onClick={() => applyPreset("thisYear")}
                    >
                      This year
                    </div>
                  </div>
                )}
              </div>
              {onResetDate && (
                <button
                  className={styles.resetButton}
                  onClick={handleReset}
                >
                  <Trash2 size={18} />
                  Reset Filter
                </button>
              )}
            </div>
          </div>

          {/* Two Calendars Side by Side */}
          <div className={styles.calendarsContainer}>
            {/* Start Date Calendar */}
            <div className={styles.calendar}>
              <div className={styles.calendarTitle}>Start Date</div>
              <div className={styles.calendarHeader}>
                <button
                  className={styles.navArrow}
                  onClick={() => navigateMonth("start", "prev")}
                >
                  ‹
                </button>
                <div className={styles.monthYearSelector}>
                  <span
                    className={styles.monthYearText}
                    onClick={() => toggleDropdown("startMonthYear")}
                  >
                    {getMonthYearText("start")}
                  </span>
                  <span>▼</span>
                  {openDropdowns.startMonthYear && (
                    <div
                      className={`${styles.monthYearDropdown} ${styles.show}`}
                    >
                      {viewMode.start === "month"
                        ? renderMonthSelector("start")
                        : renderYearSelector("start")}
                    </div>
                  )}
                </div>
                <button
                  className={styles.navArrow}
                  onClick={() => navigateMonth("start", "next")}
                >
                  ›
                </button>
              </div>
              <div className={styles.daysHeader}>
                {daysOfWeek.map((day, index) => (
                  <div key={`start-day-${index}`} className={styles.dayHeader}>
                    {day}
                  </div>
                ))}
              </div>
              <div className={styles.daysGrid}>
                {renderCalendarDays("start")}
              </div>
            </div>

            {/* End Date Calendar */}
            <div className={styles.calendar}>
              <div className={styles.calendarTitle}>End Date</div>
              <div className={styles.calendarHeader}>
                <button
                  className={styles.navArrow}
                  onClick={() => navigateMonth("end", "prev")}
                >
                  ‹
                </button>
                <div className={styles.monthYearSelector}>
                  <span
                    className={styles.monthYearText}
                    onClick={() => toggleDropdown("endMonthYear")}
                  >
                    {getMonthYearText("end")}
                  </span>
                  <span>▼</span>
                  {openDropdowns.endMonthYear && (
                    <div
                      className={`${styles.monthYearDropdown} ${styles.show}`}
                    >
                      {viewMode.end === "month"
                        ? renderMonthSelector("end")
                        : renderYearSelector("end")}
                    </div>
                  )}
                </div>
                <button
                  className={styles.navArrow}
                  onClick={() => navigateMonth("end", "next")}
                >
                  ›
                </button>
              </div>
              <div className={styles.daysHeader}>
                {daysOfWeek.map((day, index) => (
                  <div key={`end-day-${index}`} className={styles.dayHeader}>
                    {day}
                  </div>
                ))}
              </div>
              <div className={styles.daysGrid}>{renderCalendarDays("end")}</div>
            </div>
          </div>
          </div>

          <div className={styles.actionButtons}>
            <button onClick={handleCancel} className={styles.cancelButton}>
              Cancel
            </button>
            <button onClick={handleApply} className={styles.applyButton}>
              Apply
            </button>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;
